// src/components/inventory/PredictiveReorderPanel.js
// Enterprise-Grade | FlowSense AI Procurement Intelligence
// Enhanced: Glass morphism, Framer Motion, AI pulse effects, scenario planning, export, debug
// NEW: Context-Aware Seasonality AI & Price Creep Anomaly Detection
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Connected to FlowSense ML: when the reorder view (vw_smart_reorder_suggestions) is missing,
//     fails or is empty, the list comes from the FlowSense inventory-intelligence forecast (usage,
//     re-order point, EOQ, days of cover) — the panel was empty before. The source is shown.
//   • Live: the list refreshes quietly on WebSocket inventory_update / low_stock_alert /
//     work_order_completed; live pill. Pass `intel` (page overview) to skip a second fetch.
//   • FlowSense AI bar (explain Smart Reorder, the Ministry of Works view, Ask FlowSense).
//   • Draft PO works without a PO module: with no onDraftPO the draft is downloaded as CSV
//     (item, SKU, supplier, qty, unit price, line total, scenario).
//   • `initialSearch` opens the panel filtered to one item (from a low-stock alert).
//   • Fixes: NaN cost when an item has no price, red.50 row in dark mode, unhandled clipboard
//     failure, risk filter options and Healthy items hidden from the totals.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Button, HStack, VStack, Icon, useColorModeValue, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, StatArrow, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Checkbox, Slider, SliderTrack, SliderFilledTrack, SliderThumb, SliderMark,
  Tooltip, Spinner, Alert, AlertIcon, AlertDescription, Progress, Tag, TagLabel,
  TagLeftIcon, Divider, useToast, IconButton, Menu, MenuButton, MenuList, MenuItem,
  Select, Input, InputGroup, InputLeftElement, Switch, FormControl, FormLabel,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
  useDisclosure, Collapse, AlertTitle, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, Textarea
} from '@chakra-ui/react';
import {
  MdAutoGraph, MdWarning, MdCheckCircle, MdShoppingCart, MdElectricBolt,
  MdTrendingUp, MdPrecisionManufacturing, MdDateRange, MdStore, MdRefresh,
  MdDownload, MdFilterList, MdMoreVert, MdSearch, MdBugReport, MdHistory,
  MdBarChart, MdExpandMore, MdExpandLess, MdInfo, MdLocalShipping,
  MdNotifications, MdTune, MdFlashOn, MdSchedule, MdScience, MdCloud, MdAttachMoney, MdGavel
} from 'react-icons/md';
import { FaRobot, FaBrain, FaFire, FaBolt } from 'react-icons/fa';
import { motion, AnimatePresence, useAnimation, useMotionValue, useTransform } from 'framer-motion';

// --- UPDATED IMPORTS ---
import api from '../../services/api';
import purchaseOrderService from '../../services/purchaseOrderService';
import aiService from '../../services/aiService'; // Using AI service for new intelligence endpoints
// --- UPDATE (MOW+ v2)
import inventoryIntelApi from '../../services/inventoryIntelApi';
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';

/** FlowSense overview item → the suggestion shape this panel uses. */
export const fromFlowSense = (i) => ({
  item_id: i.id, name: i.name, sku: i.sku || '', category: i.category || null,
  current_stock: Number(i.quantity) || 0, daily_burn_rate: Number(i.avg_daily_usage) || 0, unit_price: Number(i.unit_price) || 0,
  reorder_point: i.effective_reorder_point ?? null, reorder_quantity: (i.suggested_order_net ?? i.suggested_order_qty) || i.eoq || 0, max_stock: null, on_order: i.on_order || 0, // --- UPDATE (Procurement): net of what is on order
  supplier_name: i.supplier || null, primary_supplier_id: null, days_until_stockout: i.days_of_cover,
  risk_level: i.risk === 'stockout' ? 'Critical' : i.risk === 'high' ? 'High Risk' : i.risk === 'reorder' ? 'Warning' : 'Healthy',
  price_anomaly: null, flowsense: { risk: i.risk, eoq: i.eoq, safety_stock: i.safety_stock, lead_time_days: i.lead_time_days, demand_30d: i.demand_30d, linked_demand_30d: i.linked_demand_30d },
});
export const flowSenseSuggestions = (overview) => (overview?.items || [])
  .filter((i) => ['stockout', 'high', 'reorder'].includes(i.risk) || (i.suggested_order_qty || 0) > 0)
  .filter((i) => i.suggested_order_net === undefined || i.suggested_order_net > 0 || i.quantity <= 0) // --- UPDATE (Procurement): skip items fully covered by open POs
  .map(fromFlowSense);
// --- END UPDATE

// ─── Motion primitives ────────────────────────────────────────
const MotionBox   = motion(Box);
const MotionFlex  = motion(Flex);
const MotionTr    = motion(Tr);
const MotionBadge = motion(Badge);

// ─── Animation variants ───────────────────────────────────────
const fadeUp  = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.07 } } };
const pulseAnim = {
  animate: { scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] },
  transition: { duration: 2.2, repeat: Infinity, ease: 'easeInOut' },
};

// ─── Outside-component constants ─────────────────────────────
const RISK_CONFIG = {
  Critical:   { color: 'red',    icon: FaFire,        chakra: 'red',    label: 'Stockout Imminent',  glow: 'rgba(229,62,62,0.35)'    },
  'High Risk':{ color: 'orange', icon: FaBolt,        chakra: 'orange', label: 'Action Required',    glow: 'rgba(221,107,32,0.3)'    },
  Warning:    { color: 'yellow', icon: MdDateRange,   chakra: 'yellow', label: 'Monitor Closely',    glow: 'rgba(214,158,46,0.25)'   },
  Healthy:    { color: 'green',  icon: MdCheckCircle, chakra: 'green',  label: 'Sufficient Stock',   glow: 'rgba(56,161,105,0.2)'    },
};

const SCENARIO_LABELS = {
  0.5: { label: 'Low Season',    color: 'blue'   },
  1.0: { label: 'Normal Ops',    color: 'green'  },
  1.5: { label: 'Elevated',      color: 'yellow' },
  2.0: { label: 'High Demand',   color: 'orange' },
  2.5: { label: 'Storm Prep',    color: 'red'    },
  3.0: { label: '🌀 Hurricane',  color: 'red'    },
};

const getScenarioLabel = (val) => {
  const closest = Object.keys(SCENARIO_LABELS).reduce((prev, curr) =>
    Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
  );
  return SCENARIO_LABELS[closest];
};

// ─── AI Pulse ring ────────────────────────────────────────────
const AIPulseRing = ({ color = 'purple', size = 40 }) => (
  <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
    <MotionBox
      position="absolute" inset={0} borderRadius="full"
      border="2px solid" borderColor={`${color}.400`}
      animate={{ scale: [1, 1.5, 1.8], opacity: [0.6, 0.3, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
    />
    <MotionBox
      position="absolute" inset={0} borderRadius="full"
      border="2px solid" borderColor={`${color}.500`}
      animate={{ scale: [1, 1.3, 1.6], opacity: [0.8, 0.4, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 0.4 }}
    />
    <Flex
      position="absolute" inset={0} borderRadius="full"
      bg={`${color}.500`} align="center" justify="center"
    >
      <Icon as={FaBrain} color="white" boxSize={`${size * 0.4}px`} />
    </Flex>
  </Box>
);

// ─── Stat card ────────────────────────────────────────────────
const AIStatCard = ({ label, value, sub, color, icon: IconComp, glow, isLoading, trend }) => {
  const bg     = useColorModeValue('white',    'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  const muted  = useColorModeValue('gray.500', 'gray.400');
  return (
    <MotionBox variants={fadeUp} whileHover={{ y: -4, boxShadow: `0 12px 40px ${glow || 'rgba(0,0,0,0.15)'}` }}
               transition={{ duration: 0.2 }}>
      <Box bg={bg} borderRadius="2xl" border="1px solid" borderColor={border}
           borderTop="3px solid" borderTopColor={`${color}.400`}
           boxShadow={`0 4px 24px ${glow || 'rgba(0,0,0,0.08)'}`} p={5} position="relative" overflow="hidden">
        {/* Background glow blob */}
        <Box position="absolute" top="-30px" right="-30px" w="100px" h="100px"
             borderRadius="full" bg={`${color}.400`} opacity={0.06} filter="blur(30px)" />
        <Stat position="relative" zIndex={1}>
          <Flex justify="space-between" align="flex-start">
            <Box flex={1}>
              <StatLabel color={muted} fontWeight="700" fontSize="xs" letterSpacing="wider" mb={2}>
                {label.toUpperCase()}
              </StatLabel>
              {isLoading
                ? <Box h="40px" w="80px" bg={`${color}.100`} borderRadius="lg" />
                : <StatNumber fontSize="3xl" fontWeight="black" color={`${color}.500`}>{value}</StatNumber>
              }
              {sub && (
                <StatHelpText mt={1} fontSize="xs">
                  {trend && <StatArrow type={trend} />}{sub}
                </StatHelpText>
              )}
            </Box>
            <MotionBox {...pulseAnim} bg={`${color}.50`} p={3} borderRadius="xl">
              <Icon as={IconComp} color={`${color}.500`} boxSize={6} />
            </MotionBox>
          </Flex>
        </Stat>
      </Box>
    </MotionBox>
  );
};

// ─── Risk badge ───────────────────────────────────────────────
const RiskBadge = React.memo(({ risk, days }) => {
  const cfg = RISK_CONFIG[risk] || RISK_CONFIG.Healthy;
  return (
    <MotionBox initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
               whileHover={{ scale: 1.05 }}>
      <VStack spacing={1} align="start">
        <Tag size="sm" colorScheme={cfg.chakra} borderRadius="full" variant="subtle"
             boxShadow={`0 0 8px ${cfg.glow}`}>
          <TagLeftIcon as={cfg.icon} boxSize={3} />
          <TagLabel fontWeight="bold" fontSize="xs">{days} Days Left</TagLabel>
        </Tag>
        <Text fontSize="xs" color={`${cfg.color}.500`} fontWeight="600">{cfg.label}</Text>
      </VStack>
    </MotionBox>
  );
});

// ─── Stock burn progress ──────────────────────────────────────
const BurnBar = React.memo(({ current, reorderPoint, maxStock }) => {
  const pct  = maxStock > 0 ? Math.min((current / maxStock) * 100, 100) : 0;
  const rPct = maxStock > 0 ? Math.min((reorderPoint / maxStock) * 100, 100) : 0;
  const color = pct <= 15 ? 'red' : pct <= 35 ? 'orange' : pct <= 60 ? 'yellow' : 'green';
  return (
    <Box position="relative" w="full">
      <Progress value={pct} size="xs" colorScheme={color} borderRadius="full" bg="gray.100" />
      {/* Reorder threshold marker */}
      <Box position="absolute" top="-1px" left={`${rPct}%`} w="2px" h="8px"
           bg="purple.500" borderRadius="full" transform="translateX(-50%)" />
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const PredictiveReorderPanel = ({ onDraftPO, intel = undefined, initialSearch = '' }) => { // --- UPDATE: + intel, initialSearch
  const [suggestions,      setSuggestions]      = useState([]);
  const [isLoading,        setIsLoading]        = useState(true);
  const [isRefreshing,     setIsRefreshing]     = useState(false);
  const [selectedItems,    setSelectedItems]    = useState(new Set());
  const [demandMultiplier, setDemandMultiplier] = useState(1.0);

  // --- AI ENHANCEMENT STATES ---
  const [seasonalInsight, setSeasonalInsight] = useState(null);
  const [rfqModalOpen, setRfqModalOpen]       = useState(false);
  const [rfqDraft, setRfqDraft]               = useState('');
  const [isDraftingRfq, setIsDraftingRfq]     = useState(false);

  // ── Enhancement state ────────────────────────────────────
  const [searchQuery,      setSearchQuery]      = useState('');
  const [riskFilter,       setRiskFilter]       = useState('All');
  const [supplierFilter,   setSupplierFilter]   = useState('All');
  const [showFilters,      setShowFilters]      = useState(false);
  const [sortBy,           setSortBy]           = useState('risk');   // 'risk' | 'days' | 'cost' | 'name'
  const [sortDir,          setSortDir]          = useState('asc');
  const [autoRefresh,      setAutoRefresh]      = useState(false);
  const [refreshInterval,  setRefreshInterval]  = useState(60);
  const [lastRefreshed,    setLastRefreshed]    = useState(null);
  const [isGenerating,     setIsGenerating]     = useState(false);
  const [debugLogs,        setDebugLogs]        = useState([]);
  const [showScenarios,    setShowScenarios]    = useState(true);
  const [source,           setSource]           = useState(null); // --- UPDATE: 'view' | 'flowsense'

  // FIX: Remove inline object to prevent useToast reference churn (fixes infinite loop)
  const toast     = useToast();
  const debugDisc = useDisclosure();
  const isMounted = useRef(true);

  // ── Theme tokens ─────────────────────────────────────────
  const bg            = useColorModeValue('white',      'gray.800');
  const pageBg        = useColorModeValue('gray.50',    'gray.900');
  const border        = useColorModeValue('gray.100',   'gray.700');
  const textMuted     = useColorModeValue('gray.500',   'gray.400');
  const tableHeader   = useColorModeValue('gray.50',    'gray.900');
  const hoverBg       = useColorModeValue('purple.50',  'whiteAlpha.100');
  const rowStripeBg   = useColorModeValue('gray.50',    'gray.750');
  const rowTextColor  = useColorModeValue('gray.800',   'white');
  const creepBg       = useColorModeValue('red.50',     'rgba(229,62,62,0.15)'); // --- UPDATE: was red.50 in dark mode too
  const debugBg       = useColorModeValue('gray.50',    'gray.900'); // --- UPDATE
  const debugCard     = useColorModeValue('white',      'gray.800'); // --- UPDATE
  const glassStyle    = {
    backdropFilter: 'blur(12px)',
    background: useColorModeValue('rgba(255,255,255,0.85)', 'rgba(26,32,44,0.85)'),
  };

  const headerGradient = useColorModeValue(
    'linear(to-r, purple.600, blue.500)',
    'linear(to-r, purple.400, blue.400)'
  );

  // ── Debug logger ─────────────────────────────────────────
  const log = useCallback((level, msg, data = null) => {
    const entry = { ts: new Date().toISOString(), level, msg, data };
    setDebugLogs(p => [...p.slice(-199), entry]);
    if (process.env.NODE_ENV === 'development') {
      const styles = { info:'color:#5A9', warn:'color:#FA0', error:'color:#F55', success:'color:#4C4' };
      console.log(`%c[PredictiveReorder][${level.toUpperCase()}] ${msg}`, styles[level] || '', data || '');
    }
  }, []);

  // ── Fetch ─────────────────────────────────────────────────
  const fetchIntelligence = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    const t0 = performance.now();
    log('info', 'Fetching AI reorder suggestions & seasonality context');
    
    try {
      // --- ENHANCEMENT: Fetch base suggestions AND seasonal weather insights concurrently ---
      const [reorderRes, seasonalRes] = await Promise.all([
        aiService.getReorderSuggestions().catch(e => {
            log('error', 'Reorder suggestions failed', e);
            return { success: false, data: [] };
        }),
        aiService.getSeasonalForecast().catch(e => {
            log('error', 'Seasonal forecast failed', e);
            return { success: false };
        })
      ]);

      if (!isMounted.current) return;

      if (reorderRes && reorderRes.success && (reorderRes.data || []).length) { // --- UPDATE: empty → FlowSense
        setSuggestions(reorderRes.data || []);
        setSource('view'); // --- UPDATE
        setLastRefreshed(new Date());
        live.markUpdated(); // --- UPDATE
        log('success', `Loaded ${(reorderRes.data || []).length} suggestions in ${(performance.now() - t0).toFixed(1)}ms`);
      } else {
        // --- UPDATE (MOW+ v2): FlowSense ML forecast instead of an empty list
        let overview = intelRef.current;
        if (!overview) { try { overview = await inventoryIntelApi.overview(); } catch (e) { log('error', 'FlowSense overview failed', e?.message); } }
        if (!isMounted.current) return;
        const fs = flowSenseSuggestions(overview);
        setSuggestions(fs);
        setSource(overview ? 'flowsense' : null);
        if (overview) { setLastRefreshed(new Date()); live.markUpdated(); }
        log(overview ? 'info' : 'warn', overview ? `Reorder view unavailable — ${fs.length} suggestions from the FlowSense ML forecast` : 'API returned success:false for suggestions');
        // --- END UPDATE
      }

      if (seasonalRes && seasonalRes.success) {
        setSeasonalInsight(seasonalRes.insight);
        // AI automatically shifts the demand multiplier based on historical weather context
        if (seasonalRes.suggestedMultiplier) {
            setDemandMultiplier(seasonalRes.suggestedMultiplier);
            log('info', `AI Adjusted Multiplier to ${seasonalRes.suggestedMultiplier}x`);
        }
      }

    } catch (err) {
      log('error', 'Fetch failed', err?.response?.data || err?.message);
      if (!silent && isMounted.current) {
        toast({
          title: 'AI Engine Error',
          description: err.response?.data?.message || 'Predictive analytics module unreachable.',
          status: 'error', duration: 6000, isClosable: true, position: 'top-right'
        });
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Strictly empty dependency array to completely prevent any reference churn loops

  // --- UPDATE (MOW+ v2): latest page overview for the fallback, live refresh, initial search
  const intelRef = useRef(intel);
  intelRef.current = intel;
  const live = useInventoryLive(() => fetchIntelligence(true));
  useEffect(() => { if (initialSearch) { setSearchQuery(initialSearch); setShowFilters(true); } }, [initialSearch]);
  useEffect(() => { if (source === 'flowsense' && intel) setSuggestions(flowSenseSuggestions(intel)); }, [intel]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  useEffect(() => {
    isMounted.current = true;
    fetchIntelligence();
    return () => { isMounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    log('info', `Auto-refresh enabled every ${refreshInterval}s`);
    const id = setInterval(() => fetchIntelligence(true), refreshInterval * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval]); 

  // ── Projections ───────────────────────────────────────────
  const activeProjections = useMemo(() => {
    let result = suggestions.map(item => {
      const adjustedBurnRate   = item.daily_burn_rate * demandMultiplier;
      const adjustedDays       = Math.floor(item.current_stock / Math.max(adjustedBurnRate, 0.1));
      const recommendedOrderQty = Math.ceil(adjustedBurnRate * 30);
      const estimatedCost       = recommendedOrderQty * (Number(item.unit_price) || 0); // --- UPDATE: no NaN

      let newRisk = item.risk_level;
      if      (adjustedDays <= 0)  newRisk = 'Critical';
      else if (adjustedDays <= 7)  newRisk = 'High Risk';
      else if (adjustedDays <= 21) newRisk = 'Warning';
      else                         newRisk = 'Healthy';

      return {
        ...item,
        projected_days:        adjustedDays,
        projected_risk:        newRisk,
        recommended_order_qty: recommendedOrderQty,
        estimated_cost:        estimatedCost,
        adjusted_burn_rate:    adjustedBurnRate,
        stock_pct:             item.max_stock > 0 ? (item.current_stock / item.max_stock) * 100 : 0,
      };
    });

    // Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.sku?.toLowerCase().includes(q) ||
        i.supplier_name?.toLowerCase().includes(q)
      );
    }
    if (riskFilter !== 'All')     result = result.filter(i => i.projected_risk === riskFilter);
    if (supplierFilter !== 'All') result = result.filter(i => i.supplier_name === supplierFilter);

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      if      (sortBy === 'risk')  cmp = a.projected_days - b.projected_days;
      else if (sortBy === 'cost')  cmp = b.estimated_cost - a.estimated_cost;
      else if (sortBy === 'name')  cmp = (a.name || '').localeCompare(b.name || '');
      else if (sortBy === 'days')  cmp = a.projected_days - b.projected_days;
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [suggestions, demandMultiplier, searchQuery, riskFilter, supplierFilter, sortBy, sortDir]);

  // ── Stats ────────────────────────────────────────────────
  const stats = useMemo(() => {
    const critical     = activeProjections.filter(i => i.projected_risk === 'Critical').length;
    const warning      = activeProjections.filter(i => i.projected_risk === 'High Risk').length;
    const healthy      = activeProjections.filter(i => i.projected_risk === 'Healthy').length;
    const atRisk       = activeProjections.filter(i => i.projected_risk === 'Warning').length;
    const estimatedCost = Array.from(selectedItems).reduce((sum, itemId) => {
      const item = activeProjections.find(i => i.item_id === itemId);
      return sum + (item ? item.recommended_order_qty * (item.unit_price || 0) : 0);
    }, 0);
    const totalCatalogueRisk = activeProjections.filter((i) => i.projected_risk !== 'Healthy').reduce((s, i) => s + i.estimated_cost, 0); // --- UPDATE: healthy items are not at risk

    return { critical, warning, healthy, atRisk, estimatedCost, totalCatalogueRisk };
  }, [activeProjections, selectedItems]);

  // ── Unique suppliers for filter ───────────────────────────
  const uniqueSuppliers = useMemo(() =>
    [...new Set(suggestions.map(i => i.supplier_name).filter(Boolean))],
    [suggestions]
  );

  // ── Selection handlers ────────────────────────────────────
  const handleSelectAll = useCallback((isChecked) => {
    if (isChecked) setSelectedItems(new Set(activeProjections.map(i => i.item_id)));
    else           setSelectedItems(new Set());
    log('info', `Select all: ${isChecked}`, { count: activeProjections.length });
  }, [activeProjections, log]);

  const handleSelect = useCallback((id, isChecked) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (isChecked) newSet.add(id);
      else           newSet.delete(id);
      return newSet;
    });
  }, []);

  // ── Draft PO ──────────────────────────────────────────────
  const handleGenerateDraft = async () => {
    if (selectedItems.size === 0) return;
    setIsGenerating(true);
    log('info', 'Generating draft PO', { count: selectedItems.size });

    const itemsToOrder = Array.from(selectedItems).map(id => {
      const item = activeProjections.find(i => i.item_id === id);
      return {
        item_id:     item.item_id,
        name:        item.name,
        quantity:    Math.max(item.reorder_quantity || 0, item.recommended_order_qty),
        unit_price:  item.unit_price,
        supplier_id: item.primary_supplier_id,
      };
    });

    // Small delay for UX feedback
    await new Promise(r => setTimeout(r, 600));

    toast({
      title: '🛒 Draft PO Generated',
      description: `${itemsToOrder.length} items bundled. ${demandMultiplier > 1 ? `Scenario: ${getScenarioLabel(demandMultiplier).label}` : ''}`,
      status: 'success', duration: 5000, isClosable: true, position: 'top-right'
    });
    log('success', 'Draft PO generated', { items: itemsToOrder.length, totalCost: stats.estimatedCost });

    setIsGenerating(false);
    if (onDraftPO) onDraftPO(itemsToOrder);
    // --- UPDATE: no PO module wired → the draft is a CSV the officer can attach to a requisition
    else downloadCsv(`flowsense-draft-po-${stamp()}.csv`, ['item_id', 'item', 'sku', 'supplier', 'quantity', 'unit_price', 'line_total', 'scenario'],
      itemsToOrder.map((i) => { const p = activeProjections.find((x) => x.item_id === i.item_id) || {}; return [i.item_id, i.name, p.sku || '', p.supplier_name || '', i.quantity, i.unit_price ?? '', ((Number(i.unit_price) || 0) * i.quantity).toFixed(2), `${demandMultiplier}x ${getScenarioLabel(demandMultiplier).label}`]; }));
    // --- END UPDATE
  };

  // --- NEW: AI Alternative Quote Handler ---
  const handleDraftAlternativeQuote = async (item) => {
    setRfqModalOpen(true);
    setIsDraftingRfq(true);
    log('info', 'Generating AI Competing RFQ', { item_id: item.item_id });
    try {
        const res = await aiService.draftAlternativeQuote(item.item_id, item.supplier_name);
        if(res.success) {
            setRfqDraft(res.draft);
            log('success', 'Alternative RFQ generated successfully');
        }
    } catch (e) {
        log('error', 'Alternative RFQ failure', e);
        setRfqDraft("Error contacting AI server. Please try again or write manually.");
    } finally {
        setIsDraftingRfq(false);
    }
  };

  // ── Export CSV ────────────────────────────────────────────
  const handleExport = useCallback(() => {
    const headers = ['Item', 'SKU', 'Supplier', 'Current Stock', 'Days Left', 'Risk', 'Suggested Qty', 'Est. Cost'];
    const rows = activeProjections.map(i => [
      i.name, i.sku, i.supplier_name, i.current_stock, i.projected_days,
      i.projected_risk, i.recommended_order_qty,
      `$${(i.recommended_order_qty * (i.unit_price || 0)).toFixed(2)}`,
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = `reorder-suggestions-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported to CSV', status: 'success', duration: 2000, position: 'top-right' });
    log('info', 'CSV exported', { rows: rows.length });
  }, [activeProjections, toast, log]);

  const getRiskUI = (risk) => RISK_CONFIG[risk] || RISK_CONFIG.Healthy;

  const scenarioInfo = getScenarioLabel(demandMultiplier);

  // ── RENDER ────────────────────────────────────────────────
  return (
    <Box bg={pageBg} minH="100vh" p={{ base: 3, md: 6 }}>

      {/* ═══ HEADER & AI CONTEXT BANNER ═══ */}
      <MotionFlex
        initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        justify="space-between" align="start" mb={8} flexWrap="wrap" gap={4}
        direction="column"
      >
        {/* NEW: SEASONALITY AI INSIGHT BANNER */}
        <AnimatePresence>
            {seasonalInsight && (
                <MotionBox 
                    w="full" 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }} 
                    exit={{ opacity: 0, height: 0 }}
                >
                    <Alert status="info" variant="solid" bgGradient="linear(to-r, cyan.600, blue.600)" borderRadius="xl" mb={6} boxShadow="md">
                        <Icon as={MdCloud} boxSize={6} color="white" mr={4} />
                        <Box flex="1">
                            <AlertTitle color="white" fontSize="md">Weather & Seasonality Intelligence</AlertTitle>
                            <AlertDescription color="cyan.50" fontSize="sm" display="block">
                                {seasonalInsight}
                            </AlertDescription>
                        </Box>
                        <Badge colorScheme="cyan" bg="white" color="blue.700" px={3} py={1} borderRadius="full" ml={4}>
                            AI Auto-Adjusted Baseline
                        </Badge>
                    </Alert>
                </MotionBox>
            )}
        </AnimatePresence>

        <Flex w="full" justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={4}>
            <AIPulseRing color="purple" size={48} />
            <VStack align="start" spacing={0}>
                <HStack>
                <Icon as={FaBrain} color="purple.400" boxSize={4} />
                <Text fontWeight="800" fontSize="xs" letterSpacing="widest" color="purple.500"
                        textTransform="uppercase">
                    FlowSense AI · Predictive Procurement
                </Text>
                </HStack>
                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="900" letterSpacing="-0.02em"
                    bgGradient={headerGradient} bgClip="text">
                Smart Reorder Engine
                </Text>
                {lastRefreshed && (
                <Text fontSize="xs" color={textMuted}>
                    Last updated {lastRefreshed.toLocaleTimeString()}
                </Text>
                )}
                {/* --- UPDATE: source + live */}
                <HStack spacing={2} pt={1}>
                  <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} />
                  {source && <Badge colorScheme={source === 'flowsense' ? 'teal' : 'purple'} borderRadius="full" fontSize="2xs">{source === 'flowsense' ? 'Source: FlowSense ML forecast' : 'Source: AI reorder view'}</Badge>}
                </HStack>
            </VStack>
            </HStack>

            <HStack spacing={3} flexWrap="wrap">
            {autoRefresh && (
                <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }}>
                <Tag colorScheme="green" borderRadius="full" size="md" px={3}>
                    <Icon as={MdSchedule} mr={1} />
                    Auto {refreshInterval}s
                </Tag>
                </MotionBox>
            )}

            <Tooltip label="Refresh Data" hasArrow>
                <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                    icon={<Icon as={MdRefresh} />} variant="ghost" colorScheme="purple"
                    borderRadius="full" size="md" aria-label="Refresh"
                    isLoading={isRefreshing}
                    onClick={() => fetchIntelligence(true)}
                />
                </MotionBox>
            </Tooltip>

            <Tooltip label="Toggle Filters" hasArrow>
                <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                    icon={<Icon as={MdFilterList} />} size="md"
                    variant={showFilters ? 'solid' : 'ghost'} colorScheme="purple"
                    borderRadius="full" aria-label="Filters"
                    onClick={() => setShowFilters(p => !p)}
                />
                </MotionBox>
            </Tooltip>

            <Tooltip label="Export CSV" hasArrow>
                <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton icon={<Icon as={MdDownload} />} variant="ghost" colorScheme="blue"
                            borderRadius="full" size="md" aria-label="Export" onClick={handleExport} />
                </MotionBox>
            </Tooltip>

            {process.env.NODE_ENV === 'development' && (
                <Tooltip label="Debug Panel" hasArrow>
                <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <IconButton icon={<Icon as={MdBugReport} />} variant="ghost" colorScheme="yellow"
                                borderRadius="full" size="md" aria-label="Debug" onClick={debugDisc.onOpen} />
                </MotionBox>
                </Tooltip>
            )}

            <Menu>
                <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />}
                            variant="ghost" borderRadius="full" size="md" />
                <MenuList shadow="xl" borderRadius="xl" fontSize="sm">
                <MenuItem icon={<Icon as={autoRefresh ? MdCheckCircle : MdSchedule}
                                        color={autoRefresh ? 'green.500' : 'gray.400'} />}
                            onClick={() => setAutoRefresh(p => !p)}>
                    {autoRefresh ? 'Disable' : 'Enable'} Auto-refresh
                </MenuItem>
                <MenuItem closeOnSelect={false}>
                    <HStack w="full">
                    <Text fontSize="sm">Interval:</Text>
                    <Select size="xs" w="80px" value={refreshInterval}
                            onChange={e => setRefreshInterval(Number(e.target.value))}>
                        <option value={30}>30s</option><option value={60}>60s</option>
                        <option value={120}>2m</option><option value={300}>5m</option>
                    </Select>
                    </HStack>
                </MenuItem>
                <MenuItem icon={<Icon as={MdScience} />} onClick={() => setShowScenarios(p => !p)}>
                    {showScenarios ? 'Hide' : 'Show'} Scenario Planner
                </MenuItem>
                </MenuList>
            </Menu>

            <MotionBox whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                leftIcon={isGenerating ? <Spinner size="xs" /> : <Icon as={MdShoppingCart} />}
                colorScheme="purple" size="lg" borderRadius="xl"
                isDisabled={selectedItems.size === 0} isLoading={isGenerating}
                loadingText="Drafting PO…"
                onClick={handleGenerateDraft}
                bgGradient="linear(to-r, purple.500, blue.500)"
                _hover={{ bgGradient: 'linear(to-r, purple.600, blue.600)', transform: 'translateY(-1px)' }}
                boxShadow="0 4px 20px rgba(128,90,213,0.45)"
                >
                Auto-Draft PO ({selectedItems.size})
                </Button>
            </MotionBox>
            </HStack>
        </Flex>
      </MotionFlex>

      {/* --- UPDATE: FlowSense AI */}
      <Box mb={6}>
        <FlowSenseInsightBar screen="reorder" data={intel || null}
          headline={[`${suggestions.length} item(s) to watch · ${stats.critical} critical · ${stats.warning} at risk at ${demandMultiplier}x (${scenarioInfo.label})`, stats.totalCatalogueRisk ? `Suggested buy for items at risk: $${stats.totalCatalogueRisk.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : 'Nothing at risk at this demand level.']}
          extra={{ suggestions: suggestions.length, critical: stats.critical, multiplier: demandMultiplier, source, top: activeProjections.slice(0, 10).map((i) => ({ name: i.name, days: i.projected_days, risk: i.projected_risk, buy: i.recommended_order_qty })) }}
          rulesAnswer={(q) => (/order|buy|reorder|re-order|scenario|hurricane|storm|critical|risk/i.test(q) ? [`Smart Reorder at ${demandMultiplier}x (${scenarioInfo.label}):`, ...activeProjections.filter((i) => i.projected_risk !== 'Healthy').slice(0, 10).map((i) => `• ${i.name}: ${i.projected_days} days left, buy ${i.recommended_order_qty}${Number(i.unit_price) ? ` ($${(i.recommended_order_qty * Number(i.unit_price)).toFixed(2)})` : ''}`), activeProjections.some((i) => i.projected_risk !== 'Healthy') ? null : 'Nothing runs short at this demand level.'].filter(Boolean).join('\n') : null)}
          questions={['What should I buy before hurricane season?', 'What will run out first?', 'Explain the inventory system for the Ministry of Works']} />
      </Box>
      {/* --- END UPDATE */}

      {/* ═══ STAT CARDS ═══ */}
      <MotionBox variants={stagger} initial="hidden" animate="show" mb={8}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
          <AIStatCard label="Critical Stockouts" value={stats.critical} color="red"
            icon={FaFire} glow="rgba(229,62,62,0.25)"
            sub="Immediate PO required" trend="increase" isLoading={isLoading} />
          <AIStatCard label="At Risk (Next 14d)" value={stats.warning} color="orange"
            icon={FaBolt} glow="rgba(221,107,32,0.2)"
            sub="Action required soon" trend="increase" isLoading={isLoading} />
          <AIStatCard label="Selected Draft Cost" value={`$${stats.estimatedCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            color="purple" icon={MdShoppingCart} glow="rgba(128,90,213,0.2)"
            sub="Est. encumbrance" isLoading={isLoading} />
          <AIStatCard label="Total Catalogue Risk" value={`$${stats.totalCatalogueRisk.toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
            color="blue" icon={MdAutoGraph} glow="rgba(49,130,206,0.2)"
            sub={`${suggestions.length} items monitored`} isLoading={isLoading} />
        </SimpleGrid>
      </MotionBox>

      {/* ═══ FILTERS (collapsible) ═══ */}
      <Collapse in={showFilters} animateOpacity>
        <MotionBox initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} mb={5}>
          <Box bg={bg} border="1px solid" borderColor={border} borderRadius="2xl" p={4}
               boxShadow="sm" {...glassStyle}>
            <Flex gap={3} flexWrap="wrap" align="center">
              <InputGroup size="sm" maxW="260px">
                <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                <Input borderRadius="xl" placeholder="Search item, SKU, supplier…"
                       value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </InputGroup>
              <Select size="sm" maxW="160px" borderRadius="xl" value={riskFilter}
                      onChange={e => setRiskFilter(e.target.value)}>
                <option value="All">All Risk Levels</option>
                {Object.keys(RISK_CONFIG).map(r => <option key={r} value={r}>{r}</option>)}
              </Select>
              <Select size="sm" maxW="200px" borderRadius="xl" value={supplierFilter}
                      onChange={e => setSupplierFilter(e.target.value)}>
                <option value="All">All Suppliers</option>
                {uniqueSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Select size="sm" maxW="160px" borderRadius="xl" value={sortBy}
                      onChange={e => setSortBy(e.target.value)}>
                <option value="risk">Sort: Risk</option>
                <option value="days">Sort: Days Left</option>
                <option value="cost">Sort: Cost</option>
                <option value="name">Sort: Name</option>
              </Select>
              <IconButton size="sm" variant="outline" borderRadius="xl" aria-label="Sort dir"
                          icon={<Icon as={sortDir === 'asc' ? MdExpandMore : MdExpandLess} />}
                          onClick={() => setSortDir(p => p === 'asc' ? 'desc' : 'asc')} />
              {(searchQuery || riskFilter !== 'All' || supplierFilter !== 'All') && (
                <Button size="xs" variant="ghost" colorScheme="red" onClick={() => {
                  setSearchQuery(''); setRiskFilter('All'); setSupplierFilter('All');
                }}>Clear Filters</Button>
              )}
            </Flex>
          </Box>
        </MotionBox>
      </Collapse>

      {/* ═══ SCENARIO PLANNER ═══ */}
      <Collapse in={showScenarios} animateOpacity>
        <MotionBox
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }} mb={6}
        >
          <Box p={6} bg={bg} borderRadius="2xl" border="1px solid" borderColor={border}
               boxShadow="sm" position="relative" overflow="hidden" {...glassStyle}>
            {/* Purple accent bar */}
            <Box position="absolute" left={0} top={0} bottom={0} w="4px"
                 bgGradient="linear(to-b, purple.500, blue.400)" borderRadius="full" />

            <Flex gap={8} align="center" flexWrap={{ base: 'wrap', md: 'nowrap' }} pl={4}>
              <Box flex={1} minW="280px">
                <HStack mb={3} color="purple.500">
                  <Icon as={MdPrecisionManufacturing} boxSize={5} />
                  <Text fontWeight="800" fontSize="sm" letterSpacing="wider" textTransform="uppercase">
                    Scenario Planning · Demand Multiplier
                  </Text>
                </HStack>
                <Text fontSize="sm" color={textMuted} mb={8} lineHeight="tall">
                  Simulate demand spikes — infrastructure emergencies, hurricane repair phases, or seasonal peaks.
                  The AI engine recalculates stockout dates in real-time based on adjusted consumption rates.
                </Text>
                <Box px={2}>
                  <Slider 
                    aria-label="demand-multiplier" value={demandMultiplier} min={0.5} max={3} step={0.1}
                    onChange={val => setDemandMultiplier(parseFloat(val.toFixed(1)))}
                    colorScheme="purple"
                  >
                    <SliderMark value={0.5} mt="3" ml="-3" fontSize="10px" color={textMuted} fontWeight="600">0.5x</SliderMark>
                    <SliderMark value={1}   mt="3" ml="-3" fontSize="10px" color="green.500" fontWeight="700">1.0x</SliderMark>
                    <SliderMark value={2}   mt="3" ml="-3" fontSize="10px" color="orange.500" fontWeight="600">2.0x</SliderMark>
                    <SliderMark value={3}   mt="3" ml="-3" fontSize="10px" color="red.500" fontWeight="700">3.0x</SliderMark>
                    <SliderTrack h="6px" borderRadius="full" bg="gray.200">
                      <SliderFilledTrack bgGradient="linear(to-r, green.400, orange.400, red.500)" />
                    </SliderTrack>
                    <Tooltip hasArrow bg="purple.500" color="white" placement="top"
                             label={`${demandMultiplier}x · ${scenarioInfo.label}`}>
                      <SliderThumb boxSize={7} boxShadow="0 2px 12px rgba(128,90,213,0.5)"
                                   border="2px solid" borderColor="purple.400">
                        <Icon as={MdTrendingUp} color="purple.500" boxSize={4} />
                      </SliderThumb>
                    </Tooltip>
                  </Slider>
                </Box>
              </Box>

              {/* Scenario display */}
              <MotionBox
                animate={{ scale: demandMultiplier > 2 ? [1, 1.02, 1] : 1 }}
                transition={{ duration: 0.8, repeat: demandMultiplier > 2 ? Infinity : 0 }}
              >
                <Box p={5} bg={`${scenarioInfo.color}.50`} borderRadius="2xl" minW="220px"
                     border="2px solid" borderColor={`${scenarioInfo.color}.200`}
                     boxShadow={`0 4px 20px rgba(0,0,0,0.08)`}>
                  <VStack spacing={2} align="start">
                    <Tag colorScheme={scenarioInfo.color} size="sm" fontWeight="bold" borderRadius="full">
                      ACTIVE SCENARIO
                    </Tag>
                    <Text fontSize="2xl" fontWeight="900" color={`${scenarioInfo.color}.600`}>
                      {scenarioInfo.label}
                    </Text>
                    <Text fontSize="3xl" fontWeight="900" color={`${scenarioInfo.color}.500`} lineHeight="1">
                      {demandMultiplier}x
                    </Text>
                    <Divider borderColor={`${scenarioInfo.color}.200`} />
                    <VStack spacing={1} align="start" fontSize="xs" color={`${scenarioInfo.color}.700`}>
                      <Text>📦 Projecting <strong>{activeProjections.length}</strong> items</Text>
                      <Text>🔴 Critical: <strong>{stats.critical}</strong></Text>
                      <Text>🟠 At Risk: <strong>{stats.warning}</strong></Text>
                    </VStack>
                  </VStack>
                </Box>
              </MotionBox>
            </Flex>
          </Box>
        </MotionBox>
      </Collapse>

      {/* ═══ DATA TABLE ═══ */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.45 }}
      >
        <Box bg={bg} borderRadius="2xl" border="1px solid" borderColor={border} overflow="hidden" boxShadow="xl" {...glassStyle}>
          {/* Gradient accent bar on top */}
          <Box h="3px" bgGradient="linear(to-r, purple.500, blue.400, cyan.400)" />

          {isLoading ? (
             <Flex direction="column" align="center" justify="center" p={20}>
               <Spinner size="xl" color="purple.500" thickness="4px" />
               <Text mt={4} color={textMuted} fontWeight="bold">AI Computing Burn Rates & Anomalies...</Text>
             </Flex>
          ) : activeProjections.length === 0 ? (
            <Flex direction="column" align="center" justify="center" p={24} gap={4}>
              <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
                <Box bg="green.50" p={6} borderRadius="full">
                  <Icon as={MdCheckCircle} boxSize={14} color="green.400" />
                </Box>
              </MotionBox>
              <Text fontWeight="800" fontSize="xl">All Stock Levels Healthy</Text>
              <Text color={textMuted} fontSize="sm" textAlign="center" maxW="sm">
                {suggestions.length > 0
                  ? 'No items match the current filters. Try adjusting your filter criteria.'
                  : 'No reorder recommendations at the current demand level.'}
              </Text>
              {suggestions.length > 0 && (
                <Button size="sm" variant="outline" colorScheme="purple" borderRadius="xl"
                        onClick={() => { setSearchQuery(''); setRiskFilter('All'); setSupplierFilter('All'); }}>
                  Clear Filters
                </Button>
              )}
            </Flex>
          ) : (
            <Table variant="simple">
              <Thead bg={tableHeader}>
                <Tr>
                  <Th w="50px">
                    <Checkbox 
                      colorScheme="purple" 
                      isChecked={selectedItems.size === activeProjections.length && activeProjections.length > 0}
                      isIndeterminate={selectedItems.size > 0 && selectedItems.size < activeProjections.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </Th>
                  <Th>
                    <HStack spacing={1} cursor="pointer" onClick={() => { setSortBy('name'); setSortDir(p => p === 'asc' ? 'desc' : 'asc'); }} _hover={{ color: 'purple.500' }}>
                      <Text>Inventory Asset</Text>
                      {sortBy === 'name' && <Icon as={sortDir === 'asc' ? MdExpandMore : MdExpandLess} boxSize={4} />}
                    </HStack>
                  </Th>
                  <Th>Stock vs Burn</Th>
                  <Th>
                    <HStack spacing={1} cursor="pointer" onClick={() => { setSortBy('days'); setSortDir(p => p === 'asc' ? 'desc' : 'asc'); }} _hover={{ color: 'purple.500' }}>
                      <Text>AI Projection</Text>
                      {sortBy === 'days' && <Icon as={sortDir === 'asc' ? MdExpandMore : MdExpandLess} boxSize={4} />}
                    </HStack>
                  </Th>
                  <Th>Primary Supplier</Th>
                  <Th isNumeric>
                    <HStack spacing={1} justify="flex-end" cursor="pointer" onClick={() => { setSortBy('cost'); setSortDir(p => p === 'asc' ? 'desc' : 'asc'); }} _hover={{ color: 'purple.500' }}>
                      <Text>Suggested Buy</Text>
                      {sortBy === 'cost' && <Icon as={sortDir === 'asc' ? MdExpandMore : MdExpandLess} boxSize={4} />}
                    </HStack>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <AnimatePresence>
                  {activeProjections.map((item, idx) => {
                    const ui = getRiskUI(item.projected_risk);
                    const isSelected = selectedItems.has(item.item_id);
                    const hasPriceCreep = item.price_anomaly && item.price_anomaly.increase_pct >= 5;

                    return (
                      <MotionTr 
                        key={item.item_id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(idx * 0.04, 0.6) }}
                        bg={hasPriceCreep ? creepBg : isSelected ? hoverBg : (idx % 2 === 0 ? 'transparent' : rowStripeBg)}
                        _hover={{ bg: hoverBg }}
                        borderLeft={item.projected_risk === 'Critical' ? '3px solid' : '3px solid transparent'}
                        borderLeftColor={item.projected_risk === 'Critical' ? 'red.400' : 'transparent'}
                      >
                        <Td>
                          <Checkbox colorScheme="purple" isChecked={isSelected} onChange={(e) => handleSelect(item.item_id, e.target.checked)} />
                        </Td>
                        <Td>
                          <HStack spacing={3}>
                            <MotionBox bg={`${ui.color}.50`} p={2} borderRadius="lg" animate={item.projected_risk === 'Critical' ? { opacity: [1, 0.5, 1] } : {}} transition={{ duration: 1.5, repeat: Infinity }}>
                              <Icon as={ui.icon} color={`${ui.color}.500`} boxSize={4} />
                            </MotionBox>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" color={rowTextColor} fontSize="sm">{item.name}</Text>
                              <HStack spacing={2}>
                                <Badge fontSize="10px" colorScheme="gray" borderRadius="full">{item.sku}</Badge>
                                {item.category && <Badge fontSize="10px" colorScheme="purple" variant="subtle" borderRadius="full">{item.category}</Badge>}
                              </HStack>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={2} minW="140px">
                            <HStack spacing={1}>
                              <Text fontSize="sm" fontWeight="bold">{item.current_stock}</Text>
                              <Text as="span" fontSize="xs" fontWeight="normal" color={textMuted}>on hand</Text>
                              {item.reorder_point && (
                                <Tooltip label={`Reorder point: ${item.reorder_point}`} hasArrow>
                                  <Icon as={MdInfo} color="purple.400" boxSize={3} cursor="help" />
                                </Tooltip>
                              )}
                            </HStack>
                            <BurnBar current={item.current_stock} reorderPoint={item.reorder_point || 0} maxStock={item.max_stock || item.current_stock * 2} />
                            <Tooltip label={`Items consumed per day (${demandMultiplier}x scenario adjusted)`} hasArrow>
                              <Badge colorScheme="gray" fontSize="10px" borderRadius="full">
                                -{Number(item.daily_burn_rate * demandMultiplier).toFixed(1)}/day
                              </Badge>
                            </Tooltip>
                          </VStack>
                        </Td>
                        <Td>
                          <RiskBadge risk={item.projected_risk} days={item.projected_days} />
                        </Td>
                        
                        {/* SUPPLIER & PRICE ANOMALY UI */}
                        <Td>
                          <VStack align="start" spacing={1}>
                              <HStack>
                                  <Icon as={MdStore} color={textMuted} />
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="150px">{item.supplier_name || 'Unassigned'}</Text>
                              </HStack>
                              {hasPriceCreep && (
                                  <Tooltip label={`Price raised from $${item.price_anomaly.baseline} to $${item.price_anomaly.current} in 6 months`} hasArrow bg="red.500">
                                      <Badge colorScheme="red" variant="solid" cursor="pointer" onClick={() => handleDraftAlternativeQuote(item)}>
                                          <HStack spacing={1}>
                                              <Icon as={MdAttachMoney} />
                                              <Text>+{item.price_anomaly.increase_pct}% Price Creep</Text>
                                          </HStack>
                                      </Badge>
                                  </Tooltip>
                              )}
                          </VStack>
                        </Td>

                        <Td isNumeric>
                          <VStack align="end" spacing={0}>
                            <Text fontWeight="black" color="purple.500" fontSize="md">+{item.recommended_order_qty}</Text>
                            <Text fontSize="xs" color={hasPriceCreep ? 'red.500' : textMuted} fontWeight={hasPriceCreep ? 'bold' : 'normal'}>
                                Est. {Number(item.unit_price) ? `$${(item.recommended_order_qty * Number(item.unit_price)).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '— (no price)'}{/* --- UPDATE: no NaN */}
                            </Text>
                          </VStack>
                        </Td>
                      </MotionTr>
                    );
                  })}
                </AnimatePresence>
              </Tbody>
            </Table>
          )}
          
          {/* Table footer */}
          {!isLoading && activeProjections.length > 0 && (
            <Flex justify="space-between" align="center" px={5} py={3}
                  borderTop="1px solid" borderColor={border} bg={tableHeader}
                  flexWrap="wrap" gap={3}>
              <HStack spacing={4}>
                <Text fontSize="sm" color={textMuted}>
                  {selectedItems.size > 0
                    ? `${selectedItems.size} of ${activeProjections.length} selected`
                    : `${activeProjections.length} items`
                  }
                </Text>
                {selectedItems.size > 0 && (
                  <MotionBox initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}>
                    <Tag colorScheme="purple" size="sm" borderRadius="full">
                      Est. cost: ${stats.estimatedCost.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                    </Tag>
                  </MotionBox>
                )}
              </HStack>
              <HStack spacing={3}>
                {selectedItems.size > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setSelectedItems(new Set())}
                          colorScheme="gray" borderRadius="xl">
                    Clear selection
                  </Button>
                )}
                <Button size="sm" colorScheme="purple" borderRadius="xl" leftIcon={<Icon as={MdDownload} />}
                        variant="outline" onClick={handleExport}>
                  Export CSV
                </Button>
                <MotionBox whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    size="sm" colorScheme="purple" borderRadius="xl"
                    leftIcon={isGenerating ? <Spinner size="xs" /> : <Icon as={MdShoppingCart} />}
                    isDisabled={selectedItems.size === 0} isLoading={isGenerating}
                    onClick={handleGenerateDraft}
                    bgGradient="linear(to-r, purple.500, blue.500)"
                    _hover={{ bgGradient: 'linear(to-r, purple.600, blue.600)' }}
                  >
                    Draft PO ({selectedItems.size})
                  </Button>
                </MotionBox>
              </HStack>
            </Flex>
          )}
        </Box>
      </MotionBox>

      {/* ═══ AI COMPETING QUOTE MODAL (PRICE CREEP) ═══ */}
      <Modal isOpen={rfqModalOpen} onClose={() => setRfqModalOpen(false)} size="2xl" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            <HStack color="red.600">
              <Icon as={FaRobot} />
              <Text>AI Generated Alternative RFQ</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isDraftingRfq ? (
              <Flex direction="column" align="center" justify="center" p={10}>
                <Spinner size="xl" color="red.500" mb={4} />
                <Text fontWeight="bold" color={textMuted}>Analyzing price discrepancies...</Text>
                <Text fontSize="sm" color={textMuted}>Drafting competitive Request For Quote (RFQ).</Text>
              </Flex>
            ) : (
              <Box>
                <Alert status="error" borderRadius="xl" mb={4}>
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    This item has experienced uncompetitive price inflation. Send this draft to alternative suppliers to find a better rate.
                  </AlertDescription>
                </Alert>
                <Textarea 
                  value={rfqDraft} 
                  onChange={(e) => setRfqDraft(e.target.value)} 
                  rows={10} 
                  borderRadius="xl"
                  _focus={{ borderColor: 'red.400', boxShadow: '0 0 0 1px var(--chakra-colors-red-400)' }}
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={border}>
            <Button variant="ghost" onClick={() => setRfqModalOpen(false)} mr={3}>Cancel</Button>
            <Button colorScheme="red" leftIcon={<Icon as={MdGavel} />} isDisabled={isDraftingRfq}
                    onClick={async () => {
                      // --- UPDATE: clipboard can be blocked (http, permissions)
                      try { await navigator.clipboard.writeText(rfqDraft); toast({ title: 'RFQ Copied to Clipboard', status: 'success', position: 'top-right' }); }
                      catch (_) { toast({ title: 'Could not copy', description: 'Select the text and copy it manually.', status: 'warning', position: 'top-right' }); return; }
                      // --- END UPDATE
                      setRfqModalOpen(false);
                    }}>
              Copy to Clipboard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ═══ DEBUG DRAWER ═══ */}
      <Drawer isOpen={debugDisc.isOpen} placement="right" onClose={debugDisc.onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack><Icon as={MdBugReport} color="yellow.400" /><Text>Debug · PredictiveReorderPanel</Text></HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>State Snapshot</Text>
                <Box bg={debugBg} borderRadius="xl" p={3} fontSize="xs" fontFamily="mono">{/* --- UPDATE: dark mode */}
                  <Text>suggestions: {suggestions.length}</Text>
                  <Text>projected: {activeProjections.length}</Text>
                  <Text>selected: {selectedItems.size}</Text>
                  <Text>multiplier: {demandMultiplier}x</Text>
                  <Text>autoRefresh: {String(autoRefresh)}</Text>
                  <Text>lastRefresh: {lastRefreshed?.toLocaleTimeString() || 'never'}</Text>
                  <Text>filter: {riskFilter} / {supplierFilter}</Text>
                  <Text>sort: {sortBy} {sortDir}</Text>
                  <Text>weather context: {seasonalInsight ? 'Loaded' : 'None'}</Text>
                  <Text>source: {source || 'none'}</Text>{/* --- UPDATE */}
                </Box>
              </Box>
              <Divider />
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="bold" fontSize="sm">Logs ({debugLogs.length})</Text>
                  <Button size="xs" colorScheme="red" variant="ghost" onClick={() => setDebugLogs([])}>Clear</Button>
                </Flex>
                <VStack maxH="400px" overflowY="auto" spacing={1} align="stretch"
                        bg={debugBg} p={2} borderRadius="xl">{/* --- UPDATE */}
                  {debugLogs.slice().reverse().map((l, i) => (
                    <Box key={i} p={2} bg={debugCard} borderRadius="lg" fontSize="xs" fontFamily="mono">{/* --- UPDATE */}
                      <Flex justify="space-between">
                        <Badge colorScheme={{ info:'blue', warn:'orange', error:'red', success:'green' }[l.level] || 'gray'} fontSize="9px">
                          {l.level}
                        </Badge>
                        <Text color="gray.400">{new Date(l.ts).toLocaleTimeString()}</Text>
                      </Flex>
                      <Text fontWeight="600" mt={0.5}>{l.msg}</Text>
                      {l.data && <Text color="gray.500" noOfLines={2}>{typeof l.data === 'string' ? l.data : JSON.stringify(l.data)}</Text>}
                    </Box>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

    </Box>
  );
};

export default PredictiveReorderPanel;